
import React, { useState, useEffect, useMemo } from "react";
import { format, addDays, startOfWeek, isSameDay, getDay } from "date-fns";
import { it } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CalendarGrid from "./CalendarGrid";
import CalendarDialogs from "./CalendarDialogs";
import SessionRequests from "./SessionRequests";
import { useTimeSlotManagement } from "@/components/calendar/hooks/useTimeSlotManagement";
import { useCalendarNavigation } from "@/components/calendar/hooks/useCalendarNavigation";
import { useIsMobile } from "@/hooks/use-mobile";
import { CalendarEvent } from "@/hooks/calendar/types";
import { CalendarSettings } from "@/lib/supabase";

interface WeeklyCalendarProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  onOpenSettings: () => void;
  settings: CalendarSettings;
  events: CalendarEvent[];
  onAddEvent?: (event: Omit<CalendarEvent, "id">) => Promise<void>;
  onDeleteEvent?: (event: CalendarEvent) => Promise<void>;
  isClientView: boolean;
  clientId: string | null;
  pendingRequestsCount: number;
  onRefreshRequests: () => void;
  onApproveRequest?: (eventId: string) => Promise<boolean>;
  onRejectRequest?: (eventId: string) => Promise<boolean>;
  onNavigateToClient?: (clientId: string) => void;
  activeView?: string;
  onViewChange?: (value: string) => void;
}

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = ({
  currentDate,
  onDateChange,
  onOpenSettings,
  settings,
  events,
  onAddEvent,
  onDeleteEvent,
  isClientView,
  clientId,
  pendingRequestsCount,
  onRefreshRequests,
  onApproveRequest,
  onRejectRequest,
  onNavigateToClient,
  activeView = "week",
  onViewChange
}) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<string>("calendar");
  
  // Memorizziamo il calcolo di weekStartDate per prevenire inutili ricalcoli
  const weekStartDate = useMemo(() => 
    startOfWeek(currentDate, { weekStartsOn: 1 }), 
    [currentDate]
  );
  
  const timeSlotManager = useTimeSlotManagement(
    events,
    settings,
    onAddEvent,
    onDeleteEvent,
    isClientView,
    clientId
  );

  const calendarNavigation = useCalendarNavigation(
    currentDate,
    onDateChange,
    settings,
    activeView,
    isMobile
  );

  const hasPendingRequests = pendingRequestsCount > 0 && !isClientView;

  const handleCalendarViewChange = (value: string) => {
    if (onViewChange) {
      onViewChange(value);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <Tabs 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="h-full flex flex-col"
      >
        <div className="flex justify-between items-center mb-0.5 mt-2">
          <TabsList className="mb-0">
            <TabsTrigger value="calendar" className="text-xs sm:text-sm">
              Calendario
            </TabsTrigger>
            {!isClientView && (
              <TabsTrigger value="requests" className="text-xs sm:text-sm flex items-center gap-1.5">
                Richieste
                {hasPendingRequests && (
                  <span className="bg-red-500 text-white text-[10px] flex items-center justify-center rounded-full h-4 min-w-4 px-1">
                    {pendingRequestsCount}
                  </span>
                )}
              </TabsTrigger>
            )}
          </TabsList>
        </div>
        
        <TabsContent value="calendar" className="flex-1 mt-1 h-full overflow-auto rounded-md">
          <CalendarGrid
            hours={calendarNavigation.hours}
            visibleDays={calendarNavigation.visibleDays}
            events={events}
            settings={settings}
            onTimeSlotClick={timeSlotManager.handleTimeSlotClick}
            onDeleteEvent={timeSlotManager.handleDeleteEvent}
            isTimeSlotUnavailable={timeSlotManager.isTimeSlotUnavailable}
            isTimeSlotInPast={timeSlotManager.isTimeSlotInPast}
          />
        </TabsContent>
        
        {!isClientView && (
          <TabsContent value="requests" className="flex-1 mt-1 px-4 py-4 overflow-auto">
            <SessionRequests 
              onSessionStatusChange={onRefreshRequests} 
              onApproveRequest={onApproveRequest}
              onRejectRequest={onRejectRequest}
              onNavigateToClient={onNavigateToClient}
            />
          </TabsContent>
        )}
      </Tabs>
      
      <CalendarDialogs
        isBookingDialogOpen={timeSlotManager.isBookingDialogOpen}
        isClientRequestDialogOpen={timeSlotManager.isClientRequestDialogOpen}
        isConflictDialogOpen={timeSlotManager.isConflictDialogOpen}
        isPastTimeSlotDialogOpen={timeSlotManager.isPastTimeSlotDialogOpen}
        deleteDialogOpen={timeSlotManager.deleteDialogOpen}
        selectedTimeSlot={timeSlotManager.selectedTimeSlot}
        conflictingEvents={timeSlotManager.conflictingEvents}
        eventToDelete={timeSlotManager.eventToDelete}
        setIsBookingDialogOpen={timeSlotManager.setIsBookingDialogOpen}
        setIsClientRequestDialogOpen={timeSlotManager.setIsClientRequestDialogOpen}
        setIsConflictDialogOpen={timeSlotManager.setIsConflictDialogOpen}
        setIsPastTimeSlotDialogOpen={timeSlotManager.setIsPastTimeSlotDialogOpen}
        setDeleteDialogOpen={timeSlotManager.setDeleteDialogOpen}
        setEventToDelete={timeSlotManager.setEventToDelete}
        handlePastTimeSlotConfirm={timeSlotManager.handlePastTimeSlotConfirm}
        handlePastTimeSlotCancel={timeSlotManager.handlePastTimeSlotCancel}
        handleConflictConfirm={timeSlotManager.handleConflictConfirm}
        handleConflictCancel={timeSlotManager.handleConflictCancel}
        handleDeleteCancel={timeSlotManager.handleDeleteCancel}
        handleDeleteConfirm={timeSlotManager.handleDeleteConfirm}
        handleBookingClose={timeSlotManager.handleBookingClose}
        handleBookingConfirm={timeSlotManager.handleBookingConfirm}
        handleClientRequestConfirm={timeSlotManager.handleClientRequestConfirm}
        clientId={clientId}
        isClientView={isClientView}
      />
    </div>
  );
};

export default WeeklyCalendar;
